/**
 * Protected Route Component
 * Redirects to login if user is not authenticated.
 * Gates on subscription status for external (non-company) users.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSubscriptionGate } from "../hooks/useSubscriptionGate";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Skip subscription gating (e.g. for /pricing which free-plan users must reach) */
  skipSubscriptionGate?: boolean;
}

export function ProtectedRoute({
  children,
  skipSubscriptionGate = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { accessGranted, isLoading: gateLoading } = useSubscriptionGate();
  const location = useLocation();

  // Show loading state while checking authentication or subscription
  if (isLoading || (isAuthenticated && !skipSubscriptionGate && gateLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect free-plan users to pricing
  if (!skipSubscriptionGate && accessGranted === false) {
    return <Navigate to="/pricing" replace />;
  }

  // Render protected content
  return <>{children}</>;
}
